import { useQuery } from "@apollo/client";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";
import { CUSTOMER_ANALYTICS_QUERY } from "@/graphql/customer-analytics";
import { CustomerEssentialMetricsCard } from "./customer-essential-metrics-card";
import { CustomerFinancialInsightsCard } from "./customer-financial-insights-card";
import { CustomerPredictiveInsightsCard } from "./customer-predictive-insights-card";
import { CustomerRiskAlerts } from "./customer-risk-alerts";
import { CustomerSeasonalPatternsCard } from "./customer-seasonal-patterns-card";
import { CustomerServiceIntelligenceCard } from "./customer-service-intelligence-card";

interface CustomerAnalyticsSectionProps {
  customerId: string;
  onAction?: (action: string, customerId: string) => void;
}

export function CustomerAnalyticsSection({ customerId, onAction }: CustomerAnalyticsSectionProps) {
  const { data, loading, error, refetch } = useQuery<CustomerAnalyticsQuery>(
    CUSTOMER_ANALYTICS_QUERY,
    {
      variables: { customerId },
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      // Cache for 5 minutes to match backend TTL
      fetchPolicy: "cache-first",
      nextFetchPolicy: "cache-first",
    }
  );

  const handleRetry = () => {
    refetch();
  };

  const handleAction = (action: string, customerId: string) => {
    // Default actions - can be customized by parent component
    switch (action) {
      case "call":
        // Open phone dialer or phone system integration
        window.open("tel:", "_self");
        break;
      case "email":
        // Open email client or email system integration
        window.open("mailto:", "_self");
        break;
      case "offer":
      case "promotion":
      case "welcome":
      case "followup":
        break;
    }

    // Call parent handler if provided
    onAction?.(action, customerId);
  };

  // Error state
  if (error && !data) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <div className="font-medium">Unable to load customer analytics</div>
              <div className="text-sm mt-1">
                {error.message || "An error occurred while fetching analytics data."}
              </div>
            </div>
            <Button onClick={handleRetry} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Partial data with error state
  if (error && data) {
    console.warn("Partial analytics data loaded with errors:", error);
  }

  const analytics = data?.customerAnalytics;

  return (
    <div className="space-y-6">
      {/* Analytics Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Customer Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Insights and intelligence to enhance customer relationships
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Error banner for partial failures */}
      {error && data && (
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Some analytics data may be incomplete. The system is working with available information.
          </AlertDescription>
        </Alert>
      )}

      {/* Risk Alerts - Always first for high priority items */}
      {analytics && (
        <CustomerRiskAlerts analytics={analytics} onAction={handleAction} loading={loading} />
      )}

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Column - Essential + Service */}
        <div className="space-y-6">
          <CustomerEssentialMetricsCard analytics={analytics ?? undefined} loading={loading} />

          <CustomerServiceIntelligenceCard analytics={analytics ?? undefined} loading={loading} />

          <CustomerSeasonalPatternsCard analytics={analytics ?? undefined} loading={loading} />
        </div>

        {/* Right Column - Financial + Predictive */}
        <div className="space-y-6">
          <CustomerFinancialInsightsCard analytics={analytics ?? undefined} loading={loading} />

          <CustomerPredictiveInsightsCard analytics={analytics ?? undefined} loading={loading} />

          {/* Future enhancements placeholder */}
          {!loading && analytics && (
            <div className="bg-muted/50 border border-dashed border-muted-foreground/20 rounded-lg p-4 text-center">
              <div className="text-sm text-muted-foreground">More insights available</div>
              <div className="text-xs text-muted-foreground mt-1">
                Custom metrics and advanced analytics coming soon
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Performance Notice */}
      {analytics && !loading && (
        <div className="text-center">
          <div className="text-xs text-muted-foreground">
            Analytics updated {new Date(analytics.lastUpdated).toLocaleString()} • Data cached for
            performance
          </div>
        </div>
      )}
    </div>
  );
}

// Export individual components for flexibility
export {
  CustomerEssentialMetricsCard,
  CustomerServiceIntelligenceCard,
  CustomerFinancialInsightsCard,
  CustomerRiskAlerts,
  CustomerSeasonalPatternsCard,
  CustomerPredictiveInsightsCard,
};
